import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { UX2, constants, utils as CoreUtils } from '@wsb/guac-widget-core';
import { TABLET, MOBILE } from '../../common/constants/device';
import {
  dataAids,
  getGalleryDataAid,
  getGalleryCellDataAid,
  getGalleryDataRouteProps
} from '../../common/constants/dataAids';
import wrapWithDeviceDetection from '../../common/wrapWithDeviceDetection';
import Masonry from '@wsb/guac-widget-shared/lib/components/Masonry';
import RenderLightbox from '../../common/renderLightbox';
import GalleryImage from '../../common/components/GalleryImage';

const ImageMaxWidth = {
  [MOBILE]: 182,
  [TABLET]: 370
};
const { SM_MIN, MD_MIN } = constants.breakpoints;
const {
  postRender: { Actions: postRenderActions, monitor }
} = CoreUtils;
const sizeMap = {
  mobile: { columns: 2, gutter: 0 },
  tablet: [
    { mq: `${SM_MIN}px`, columns: 2, gutter: 0 },
    { mq: `${MD_MIN}px`, columns: 3, gutter: 0 }
  ]
};

export default wrapWithDeviceDetection(
  class Gallery4 extends Component {
    constructor() {
      super(...arguments);
      this.state = {
        showLightbox: false,
        selectedIndex: 0,
        page: 1
      };
      this.hideLightbox = this.hideLightbox.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.loadMore = this.loadMore.bind(this);
      this.hasNextPage = this.hasNextPage.bind(this);
    }

    static get propTypes() {
      return {
        id: PropTypes.string,
        category: PropTypes.string,
        device: PropTypes.string,
        galleryImages: PropTypes.array.isRequired,
        renderAsThumbnail: PropTypes.bool,
        renderMode: PropTypes.string,
        section: PropTypes.string,
        size: PropTypes.string,
        staticContent: PropTypes.object
      };
    }

    static get defaultProps() {
      return {
        category: 'neutral',
        section: 'default'
      };
    }

    componentDidMount() {
      const { id } = this.props;
      monitor(id, postRenderActions.SCROLL_TO_URL_FRAGMENT);
    }

    hideLightbox() {
      this.setState({
        showLightbox: false
      });
    }

    handleClick(index) {
      this.setState({
        showLightbox: true,
        selectedIndex: index
      });
    }

    loadMore() {
      this.setState({ page: this.state.page + 1 });
    }

    renderSeeMoreText() {
      const { staticContent } = this.props;
      const styles = { paddingTop: 'xlarge', textAlign: 'center' };

      return (
        <UX2.Element.Block style={ styles }>
          <UX2.Element.MoreLink.Expand
            role='button'
            tabIndex='0'
            onClick={ this.loadMore }
            data-aid={ dataAids.GALLERY_MORE_BUTTON }
            children={ staticContent.showMore }
            data-edit-interactive={ true }
          />
        </UX2.Element.Block>
      );
    }

    hasNextPage() {
      return !!this.props.galleryImages[this.state.page];
    }

    renderContent(imageData, imagesLen, externalLink) {
      const { device = TABLET, galleryImages } = this.props;
      const Cell = UX2.Component.Grid.Cell;
      let pushXs, pushMd;
      if (imagesLen < 3 && galleryImages.length === 1) {
        pushXs = imagesLen === 1 ? 3 : null;
        pushMd = imagesLen === 1 ? 4 : 2;
      }

      const imageWidth = ImageMaxWidth[device];

      const imageProps = {
        'imageData': {
          ...imageData,
          outputWidth: imageWidth
        },
        'onClick': !externalLink
          ? () => {
            this.handleClick(imageData.index);
          }
          : null,
        'data-aid': getGalleryDataAid(imageData.index),
        'style': { cursor: 'pointer', width: '100%', height: 'auto' },
        ...getGalleryDataRouteProps(imageData.index, { isImage: true })
      };

      return (
        <Cell
          xs={ 6 }
          md={ 4 }
          pushXs={ pushXs }
          pushMd={ pushMd }
          style={{ padding: 'xxsmall', width: '100%' }}
          data-aid={ getGalleryCellDataAid(imageData.index) }
        >
          <GalleryImage imageProps={ imageProps } externalLink={ externalLink } background={ false } />
        </Cell>
      );
    }

    renderFakeContent(image) {
      const containerStyles = {
        padding: 'xxsmall',
        width: '100%',
        display: 'inline-block',
        margin: '0.25em'
      };

      return (
        <UX2.Element.Container style={ containerStyles }>
          <UX2.Element.Image
            data-aid={ dataAids.FAKE_THUMBNAIL_NAV_LIST }
            style={{ cursor: 'pointer', width: '100%', height: 'auto' }}
            imageData={ image }
          />
        </UX2.Element.Container>
      );
    }

    renderFakeMasonry(images) {
      const containerStyles = {
        '-moz-column-width': '22em',
        '-webkit-column-width': '22em',
        '-moz-column-gap': '1em',
        '-webkit-column-gap': '1em'
      };

      return <UX2.Element.Container style={ containerStyles }>{images}</UX2.Element.Container>;
    }

    renderMasonry(images) {
      const { device, renderMode } = this.props;
      return (
        <UX2.Element.Block style={{ marginLeft: '-xxsmall', marginRight: '-xxsmall' }}>
          <Masonry sizeMap={ sizeMap } device={ device } renderMode={ renderMode }>
            {images}
          </Masonry>
        </UX2.Element.Block>
      );
    }

    render() {
      const { device, galleryImages, renderAsThumbnail, renderMode, size } = this.props;
      const Grid = UX2.Component.Grid;
      const renderFakeThumbnail = (device === 'mobile' || size === 'xs') && renderAsThumbnail;

      if (renderMode === 'EDIT' && this.state.page > 1) this.setState({ page: 1 });

      const allImages = galleryImages.slice(0, this.state.page).map(images => {
        return images.map(({ image, externalLink }) =>
          renderFakeThumbnail
            ? this.renderFakeContent(image)
            : this.renderContent(image, images.length, externalLink)
        );
      });

      const masonryComponent = renderFakeThumbnail
        ? this.renderFakeMasonry(allImages)
        : this.renderMasonry(allImages);

      return (
        <React.Fragment>
          <Grid gutter={ false } bottom={ false }>
            {masonryComponent}
          </Grid>

          {RenderLightbox.call(this)}

          {this.hasNextPage() ? this.renderSeeMoreText() : null}
        </React.Fragment>
      );
    }
  }
);
